import { type ReactElement, type ReactNode, useState } from 'react';
import { type RideSupportDetail, type SupportDeskChannel, type SupportTicketCategory, type WireMoney } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatDistance, formatDuration, formatMoney, type FormatContext } from '@vello/format';
import { Badge, Button, Notice, Panel, SectionTitle, Text } from '@vello/web-theme';
import {
  cashOutcomeTone,
  cashSettleOffer,
  finalFareTone,
  formatMarketInstant,
  marketTimeZone,
  renderableQuantity,
  rideCashState,
  rideMoneyAuthority,
  rideTicketDefaultCategory,
  routeTone,
  settleRideCash,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { RideCashSettleDialog } from './ride-cash-settle-dialog';
import { RideTicketDialog } from './ride-ticket-dialog';
import { DetailRow } from './detail-row';
import { usePolicyVersionText } from './policy-version-text';

function MoneySection({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <Panel>
      <SectionTitle>{title}</SectionTitle>
      {children}
    </Panel>
  );
}

const SubTitle = ({ children }: { children: ReactNode }): ReactElement => (
  <Text as="h3" role="label" tone="secondary">{children}</Text>
);

export interface RideMoneyContext {
  /** The ride as last read (undefined while loading or after a failed read — the hook then renders nothing). */
  readonly detail: RideSupportDetail | undefined;
  /** Re-read the ride after an action changed it. */
  readonly reload: () => Promise<void>;
}

const NOTHING = { actions: null, panels: null, dialogs: null } as const;

/**
 * The ride page's money block (W8-FINAL-FARE-AND-CASH, D438; findings A-03, A-04): the panels (quoted → final with the
 * revision's reason and rule, the cash outcome, the priced route) and the two actions with their in-app dialogs —
 * "registrar recebimento" (the page's one PRIMARY action, only for a completed cash ride without a recorded outcome, under
 * `payment:settle`) and "abrir chamado" (secondary, under `support_ticket:manage`). Returned as three pieces so the ride
 * screen places them — header actions, grid panels, dialogs — without owning their logic. A hook called before the
 * screen's loading returns, so the dialogs keep their state across re-reads of the ride. Instants render in the ride's
 * market time zone; money from integer minor units; distance and duration through `@vello/format` in the console's
 * formatting context.
 */
export function useRideMoney(ctx: RideMoneyContext): { readonly actions: ReactNode; readonly panels: ReactNode; readonly dialogs: ReactNode } {
  const { runtime, grants, markets } = useAdmin();
  const { navigate } = useRouter();
  const { toast } = useConsole();
  const { t, lang } = useT();
  const { fail, v, reason, refusal: refusalOf } = useVocab();
  const words = usePolicyVersionText();
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleError, setSettleError] = useState<string | undefined>(undefined);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketBusy, setTicketBusy] = useState(false);
  const [ticketError, setTicketError] = useState<string | undefined>(undefined);
  /**
   * The settlement this operator just recorded. The operator route books the ledger but writes no cash-collection record,
   * so the re-read ride still reads "not recorded": the page remembers it (display only) instead of offering the action
   * again — a second post would be a DUPLICATE or, with another amount, a LEDGER_CONFLICT.
   */
  const [settledHere, setSettledHere] = useState<{ readonly rideId: string; readonly amount: WireMoney; readonly ledger: string } | undefined>(undefined);

  const { detail, reload } = ctx;
  if (detail === undefined) return NOTHING;
  const r = detail.ride;
  const tz = marketTimeZone(r.marketId, markets);
  const format: FormatContext = { locale: lang, timeZone: tz ?? 'UTC', measurement: 'metric' };
  const money = (w: WireMoney): string => (/^-?\d+$/.test(w.amountMinor) && isIso4217Currency(w.currency) ? formatMoney({ amountMinor: BigInt(w.amountMinor), currency: w.currency }, format) : '—');
  const when = (instant: string | undefined): string => formatMarketInstant(instant, tz, lang);
  const { maySettle, mayOpenTicket } = rideMoneyAuthority(grants);
  const settledNow = settledHere !== undefined && settledHere.rideId === r.rideId ? settledHere : undefined;
  const offered = cashSettleOffer(detail, maySettle);
  const offer = settledNow !== undefined ? ({ offer: false, block: 'ALREADY_RECORDED' } as const) : offered;
  const cash = rideCashState(detail);

  const settle = async (amount: WireMoney): Promise<void> => {
    if (settleBusy || !offer.offer) return;
    setSettleBusy(true);
    setSettleError(undefined);
    const res = await settleRideCash(runtime, { rideId: r.rideId, driverId: offer.driverId, amount });
    setSettleBusy(false);
    if (!res.ok) {
      setSettleError(refusalOf('refusal.admin.ride_cash_settle', res.error) ?? t('rideCash.settle.failed', { code: fail(res.error) }));
      return;
    }
    setSettleOpen(false);
    const ledger = res.value.ledger;
    setSettledHere({ rideId: r.rideId, amount, ledger });
    if (ledger === 'UNBALANCED') toast({ message: t('rideCash.settle.unbalanced'), tone: 'warning' });
    else if (ledger === 'DUPLICATE') toast({ message: t('rideCash.settle.duplicate'), tone: 'neutral' });
    else toast({ message: t('rideCash.settle.ok', { debt: /^\d+$/.test(res.value.driverDebtMinor) ? money({ amountMinor: res.value.driverDebtMinor, currency: amount.currency }) : '—' }), tone: 'success' });
    await reload();
  };

  const openTicket = async (input: { readonly channel: SupportDeskChannel; readonly category: SupportTicketCategory }): Promise<void> => {
    if (ticketBusy) return;
    setTicketBusy(true);
    setTicketError(undefined);
    const res = await runtime.supportDesk.openForCaller({ market: r.marketId, channel: input.channel, category: input.category, subject: { kind: 'RIDE', id: r.rideId } });
    setTicketBusy(false);
    if (!res.ok) {
      setTicketError(t('rideTicket.failed', { code: fail(res.error) }));
      return;
    }
    setTicketOpen(false);
    toast({ message: t('rideTicket.opened', { code: r.code }), tone: 'success' });
    navigate('case_detail', { id: res.value.ticket.ticketId });
  };

  const actions = (
    <>
      {mayOpenTicket && (
        <Button variant="secondary" onClick={() => { setTicketError(undefined); setTicketOpen(true); }} disabled={ticketBusy}>{t('rideTicket.action')}</Button>
      )}
      {offer.offer && (
        <Button variant="primary" icon="badgeCheck" onClick={() => { setSettleError(undefined); setSettleOpen(true); }} disabled={settleBusy}>{t('rideCash.settle.action')}</Button>
      )}
    </>
  );

  const m = detail.money;
  const f = m?.finalFare;
  const route = detail.route;
  const quantity = (n: number | undefined, render: (v: number) => string): string => (renderableQuantity(n) ? render(n) : '—');
  const distance = (n: number | undefined): string => quantity(n, (v) => formatDistance(v, format));
  const duration = (n: number | undefined): string => quantity(n, (v) => formatDuration(v, format));
  // Why "registrar recebimento" is not offered on a completed ride whose cash is not recorded — said, not hidden silently.
  const settleNote = r.status === 'completed' && !offer.offer && (offer.block === 'NO_PERMISSION' || offer.block === 'CASH_UNKNOWN') && cash.kind !== 'recorded' ? v('ride.cash_record_block', offer.block) : undefined;

  const panels = (
    <>
      <MoneySection title={t('rideMoney.section')}>
        {m === undefined ? (
          <Notice tone="neutral">{t('rideMoney.unavailable')}</Notice>
        ) : (
          <>
            <DetailRow mono label={t('rideMoney.quoted')} value={money(m.quoted)} />
            {/* D465b: "Valor combinado" - the price the rider and the driver agreed, with the policy version it ran under. */}
            {m.agreedPrice !== undefined && (
              <>
                <DetailRow mono label={t('rideMoney.agreed')} value={money(m.agreedPrice.total)} />
                <DetailRow mono={false} label={t('rideMoney.agreedBy')} value={v('ride.agreed_price_source', m.agreedPrice.source)} />
                <DetailRow mono={false} label={t('rideMoney.policy')} value={<span title={m.agreedPrice.policyVersion}>{words.policy(m.agreedPrice.policyVersion)}</span>} />
              </>
            )}
            {f === undefined ? (
              <DetailRow mono label={t('rideMoney.final')} value={<Text role="caption" tone="tertiary">{t('rideMoney.final.none')}</Text>} />
            ) : (
              <>
                <DetailRow mono label={t('rideMoney.final')} value={money(f.final)} />
                <div className="vello-admin-a-pad-y">
                  <Badge tone={finalFareTone(f)}>{v('ride.final_fare_direction', f.revised ? f.direction : 'NONE')}</Badge>
                </div>
                <DetailRow mono={false} label={t('rideMoney.finalReason')} value={reason(f.reasonCode)} />
                {/* D855: which trigger of the rule fired and which bound held the fare. */}
                {f.triggerCode !== undefined && <DetailRow mono={false} label={t('rideMoney.trigger')} value={reason(f.triggerCode)} />}
                {f.capCode !== undefined && <DetailRow mono={false} label={t('rideMoney.cap')} value={reason(f.capCode)} />}
                {/* D463: the time the driver held the trip, its charge (inside the final) and why — never a silent surcharge. */}
                {f.waiting !== undefined && (
                  <DetailRow mono={false} label={t('rideMoney.waiting')} value={`${money(f.waiting.charge)} · ${duration(f.waiting.measuredSeconds)} · ${reason(f.waiting.reasonCode)}`} />
                )}
                {f.policyVersion !== undefined && <DetailRow mono={false} label={t('rideMoney.policy')} value={<span title={f.policyVersion}>{words.policy(f.policyVersion)}</span>} />}
                {(renderableQuantity(f.executed.distanceMeters) || renderableQuantity(f.executed.durationSeconds)) && (
                  <DetailRow mono label={t('rideMoney.executed')} value={`${distance(f.executed.distanceMeters)} · ${duration(f.executed.durationSeconds)}`} />
                )}
                <DetailRow mono={false} label={t('rideMoney.evidence')} value={v('ride.distance_evidence', f.executed.distanceEvidence, { count: f.executed.sampleCount })} />
              </>
            )}
            <SubTitle>{t('rideMoney.cash.title')}</SubTitle>
            {cash.kind === 'unavailable' ? (
              <Notice tone="neutral">{t('rideMoney.cash.unavailable')}</Notice>
            ) : cash.kind === 'not_recorded' ? (
              settledNow !== undefined ? (
                <Notice tone={settledNow.ledger === 'UNBALANCED' ? 'warning' : 'success'}>
                  <span>{t('rideCash.settled.here', { amount: money(settledNow.amount), ledger: v('ride.cash_settlement_ledger_state', settledNow.ledger) })}</span>
                </Notice>
              ) : (
                <Text role="caption" tone="tertiary">{t('rideMoney.cash.notRecorded')}</Text>
              )
            ) : (
              <>
                <div className="vello-admin-a-pad-y">
                  <Badge tone={cashOutcomeTone(cash.outcome)}>{v('ride.cash_outcome', cash.outcome)}</Badge>
                </div>
                <DetailRow mono label={t('rideMoney.cash.received')} value={money(cash.received)} />
                {cash.due !== undefined && <DetailRow mono label={t('rideMoney.cash.due')} value={money(cash.due)} />}
                {/* D854 (L2-013): the part of the fare the rider's Saldo Vello credit paid, and what Vello pays the driver for it. */}
                {cash.creditApplied !== undefined && <DetailRow mono label={t('rideMoney.cash.creditApplied')} value={money(cash.creditApplied)} />}
                {cash.platformPaid !== undefined && <DetailRow mono label={t('rideMoney.cash.platformPaid')} value={money(cash.platformPaid)} />}
                {cash.reasonCode !== undefined && <DetailRow mono={false} label={t('rideMoney.cash.reason')} value={v('cash.shortfall_reason', cash.reasonCode)} />}
                <DetailRow mono label={t('rideMoney.cash.commission')} value={money(cash.commission)} />
                <DetailRow mono label={t('rideMoney.cash.confirmedAt')} value={when(cash.confirmedAt)} />
                <DetailRow mono label={t('rideMoney.cash.booked')} value={<Badge tone={cash.booked ? 'success' : 'warning'}>{t(cash.booked ? 'rideMoney.cash.booked.yes' : 'rideMoney.cash.booked.no')}</Badge>} />
              </>
            )}
            {settleNote !== undefined && (
              <div>
                <Text role="caption" tone="tertiary"><span role="note">{settleNote}</span></Text>
              </div>
            )}
          </>
        )}
      </MoneySection>

      <MoneySection title={t('rideMoney.route.section')}>
        {route === undefined ? (
          <Notice tone="neutral">{t('rideMoney.route.none')}</Notice>
        ) : (
          <>
            {route.degraded && (
              <div className="vello-admin-a-pad-y">
                <Badge tone={routeTone(route)}>{t('rideMoney.route.degraded')}</Badge>
              </div>
            )}
            <DetailRow mono={false} label={t('rideMoney.route.provider')} value={v('ride.route_provider', route.providerId)} />
            <DetailRow mono={false} label={t('rideMoney.route.reason')} value={reason(route.reasonCode)} />
            <DetailRow mono label={t('rideMoney.route.distance')} value={distance(route.distanceMeters)} />
            <DetailRow mono label={t('rideMoney.route.duration')} value={duration(route.durationSeconds)} />
            <DetailRow mono label={t('rideMoney.route.straightLine')} value={distance(route.straightLineMeters)} />
            {route.policyVersion !== undefined && <DetailRow mono={false} label={t('rideMoney.route.policy')} value={<span title={route.policyVersion}>{words.policy(route.policyVersion)}</span>} />}
          </>
        )}
      </MoneySection>
    </>
  );

  const dialogs = (
    <>
      {offer.offer && (
        <RideCashSettleDialog
          open={settleOpen}
          rideCode={r.code}
          due={offer.due}
          busy={settleBusy}
          error={settleError}
          money={money}
          onCancel={() => setSettleOpen(false)}
          onConfirm={(amount) => void settle(amount)}
        />
      )}
      <RideTicketDialog
        open={ticketOpen}
        rideCode={r.code}
        defaultCategory={rideTicketDefaultCategory(cash)}
        busy={ticketBusy}
        error={ticketError}
        onCancel={() => setTicketOpen(false)}
        onConfirm={(input) => void openTicket(input)}
      />
    </>
  );

  return { actions, panels, dialogs };
}
